import { DatesRangeValue } from '@mantine/dates';
import { useGetHotelSchedules } from 'api-hooks/cro/query';
import { EditSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import { formatDate } from 'common/utils/string';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import Separator from 'components/common/separator';
import Button from 'components/elements/buttons/button';
import DatePickerInput from 'components/elements/date-picker-input';
import PaginationComponent from 'components/elements/pagination-component';
import TableComponent from 'components/elements/table-component/table-component';
import Text from 'components/elements/text';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDrawer, { DrawerProvider } from 'hooks/use-drawer';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import {
  CardContainer,
  TableContainer,
  TopContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import HotelScheduleForm from '../components/hotel-schedule/hotel-schedule-form';
import useHotelScheduleTableList from '../hooks/use-hotel-schedule-table-list';

const monthToMilisecond = 1 * 14 * 24 * 60 * 60 * 1000;

export default function HotelScheduleTable() {
  const { t } = useTranslation();
  const today = new Date();

  const [date, setDate] = React.useState<DatesRangeValue>([
    new Date(today.getTime() - monthToMilisecond),
    today,
  ]);

  const [page, setPage] = React.useState(1);

  const check_in_before = date[1] ? formatDate(date[1]) : null;
  const check_in_after = date[0] ? formatDate(date[0]) : null;

  const _columns = useHotelScheduleTableList();

  const {
    data,
    isLoading,
    isFetching,
    error,
    extras: [{ filters = [], setFilters }, { columns }],
  } = useComposedQuery(
    useGetHotelSchedules,
    {
      params: {
        page,
        limit: -1,
        filter: {
          check_in_after,
          check_in_before,
        },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters.filter(
        (curr) =>
          curr.name !== 'check_in_after' && curr.name !== 'check_in_before',
      );
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const drawer = useDrawer();
  const onClickEdit = React.useCallback(() => {
    drawer.showCustom({
      render(onClose) {
        return (
          <DrawerProvider>
            <DrawerHeader onClose={onClose} title="Update Hotel Schedule" />
            <DrawerBody>
              <HotelScheduleForm data={data?.data || []} onClose={onClose} />
            </DrawerBody>
          </DrawerProvider>
        );
      },
    });
  }, [data?.data, drawer]);

  const permission = useGetCrudPermission('hotel-sales');

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <CardContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text variant="LabelLarge">
            {t('common:check_in_extra_schedule', { extra: t('common:hotel') })}
          </Text>
          <div style={{ display: 'flex', gap: 16, flexDirection: 'row' }}>
            <DatePickerInput
              type="range"
              value={date}
              onChange={(value: DatesRangeValue) => {
                setDate(value);
              }}
              miw={230}
              maw={230}
              clearable
            />
            {!!data?.data?.length && (
              <Button
                endEnhancer={(color, size) => (
                  <EditSVG color={color} size={size} />
                )}
                onClick={onClickEdit}
              >
                {t('common:update')}
              </Button>
            )}
          </div>
        </div>
        <TopContainer>
          <QueryFilter filters={filters || []} />
        </TopContainer>
        <Separator gap={16} />
        <TableContainer
          $style={{
            maxHeight: '300px',
          }}
        >
          <TableComponent
            columns={columns}
            isLoading={isLoading || isFetching}
            data={data?.data || []}
            rowLink={(original) => {
              if (!permission.view) return undefined;
              return {
                pathname: NavigationRoutes.HotelSalesView,
                query: { id: original.original.saleId },
              };
            }}
          />
        </TableContainer>
        <PaginationComponent
          page={page}
          onPageChange={(page) => {
            setPage(page);
          }}
          meta={data?.meta}
        />
      </CardContainer>
    </FilterHandleParentProvider>
  );
}
